import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from './lib/axiosClient';
import useAutoRefresh from './hooks/useAutoRefresh';

const CATEGORIES = ['General', 'Parts', 'Oils & Fluids', 'Retail', 'Equipment', 'Supplies', 'Beverages', 'Other'];

function Inventory() {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({ total: 0, totalValue: 0, totalCost: 0, lowStock: 0, outOfStock: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table'); // table, grid, categories, lowstock
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [selectedIds, setSelectedIds] = useState([]);
  const [stockAdj, setStockAdj] = useState({ id: null, name: '', current: 0, amount: '', reason: '' });
  const [showScanner, setShowScanner] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const videoRef = useRef(null);
  const scannerStream = useRef(null);

  const emptyForm = {
    name: '', sku: '', barcode: '', category: 'General', description: '',
    retail_price: '', cost: '', current_stock: '', low_stock_threshold: '5',
    track_inventory: true, supplier_name: '', is_active: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [prodRes, statsRes] = await Promise.all([
        axios.get('/api/inventory'),
        axios.get('/api/inventory/stats'),
      ]);
      if (prodRes.data?.success) setProducts(prodRes.data.products || []);
      if (statsRes.data?.success) setStats(statsRes.data.stats);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useAutoRefresh(loadData, 20000);  const handleSave = async () => {
    if (!form.name.trim()) return alert('Product name is required');
    try {
      const payload = {
        name: form.name, sku: form.sku, barcode: form.barcode,
        category: form.category, description: form.description,
        retail_price: form.retail_price, cost: form.cost,
        current_stock: form.current_stock, low_stock_threshold: form.low_stock_threshold,
        track_inventory: form.track_inventory, supplier_name: form.supplier_name,
        is_active: form.is_active,
      };
      if (editing) {
        await axios.put('/api/inventory/' + editing, payload);
      } else {
        await axios.post('/api/inventory', payload);
      }
      setShowForm(false); setEditing(null); setForm(emptyForm); loadData();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleEdit = (p) => {
    setForm({
      name: p.name || '', sku: p.sku || '', barcode: p.barcode || '',
      category: p.category || 'General', description: p.description || '',
      retail_price: p.retail_price || '', cost: p.cost || '',
      current_stock: p.current_stock || '', low_stock_threshold: p.low_stock_threshold || '5',
      track_inventory: p.track_inventory !== false, supplier_name: p.supplier_name || '',
      is_active: p.is_active !== false,
    });
    setEditing(p.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete('/api/inventory/' + id); loadData(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm('Delete ' + selectedIds.length + ' products?')) return;
    try { await axios.post('/api/inventory/bulk-delete', { ids: selectedIds }); setSelectedIds([]); loadData(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleStockAdjust = async () => {
    if (!stockAdj.amount) return;
    try {
      await axios.patch('/api/inventory/' + stockAdj.id + '/stock', {
        adjustment: parseInt(stockAdj.amount), reason: stockAdj.reason,
      });
      setStockAdj({ id: null, name: '', current: 0, amount: '', reason: '' }); loadData();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const toggleSelect = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(p => p.id));

  // ── Helpers ──
  const getMargin = (p) => {
    var r = parseFloat(p.retail_price) || 0;
    var c = parseFloat(p.cost) || 0;
    if (r === 0) return 0;
    return Math.round(((r - c) / r) * 100);
  };
  const getProfit = (p) => (parseFloat(p.retail_price) || 0) - (parseFloat(p.cost) || 0);
  const isLow = (p) => p.track_inventory !== false && (p.current_stock || 0) > 0 && (p.current_stock || 0) <= (p.low_stock_threshold || 5);
  const isOut = (p) => p.track_inventory !== false && (p.current_stock || 0) === 0;
  const daysSince = (d) => { if (!d) return null; return Math.floor((Date.now() - new Date(d).getTime()) / 86400000); };

  const filtered = useMemo(() => {
    let list = products.filter(p => {
      var ms = !search || p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.sku && p.sku.toLowerCase().includes(search.toLowerCase())) ||
        (p.barcode && p.barcode.toLowerCase().includes(search.toLowerCase())) ||
        (p.supplier_name && p.supplier_name.toLowerCase().includes(search.toLowerCase()));
      var mc = catFilter === 'All' || p.category === catFilter;
      return ms && mc;
    });
    list.sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'price') return (parseFloat(b.retail_price) || 0) - (parseFloat(a.retail_price) || 0);
      if (sortBy === 'stock') return (a.current_stock || 0) - (b.current_stock || 0);
      if (sortBy === 'value') return ((parseFloat(b.retail_price) || 0) * (b.current_stock || 0)) - ((parseFloat(a.retail_price) || 0) * (a.current_stock || 0));
      if (sortBy === 'margin') return getMargin(b) - getMargin(a);
      return 0;
    });
    return list;
  }, [products, search, catFilter, sortBy]);

  const categories = useMemo(() => ['All', ...Array.from(new Set(products.map(p => p.category || 'General'))).sort()], [products]);
  const grouped = useMemo(() => {
    var g = {};
    filtered.forEach(p => { var c = p.category || 'General'; if (!g[c]) g[c] = []; g[c].push(p); });
    return g;
  }, [filtered]);

  const lowStockItems = useMemo(() => products.filter(p => isLow(p) || isOut(p)).sort((a, b) => (a.current_stock || 0) - (b.current_stock || 0)), [products]);
  const margin = stats.totalValue > 0 ? (((stats.totalValue - (stats.totalCost || 0)) / stats.totalValue) * 100).toFixed(0) : 0;

  // ── Barcode Scanner ──
  const startScanner = async () => {
    setShowScanner(true);
    try {
      var stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      scannerStream.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
    } catch (e) {
      alert('Camera access denied. You can type the barcode manually.');
      setShowScanner(false);
    }
  };
  const stopScanner = () => {
    if (scannerStream.current) { scannerStream.current.getTracks().forEach(t => t.stop()); scannerStream.current = null; }
    setShowScanner(false);
  };
  const handleBarcodeLookup = (code) => {
    var found = products.find(p => p.barcode === code || p.sku === code);
    if (found) { handleEdit(found); stopScanner(); }
    else { alert('No product found with code: ' + code); }
  };

  // ── Styles ──
  const S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px', padding: '20px', transition: 'border-color 0.2s' },
    pill: (active) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: active ? '#ff6b2b' : 'transparent', color: active ? '#fff' : '#888' }),
    viewBtn: (active) => ({ padding: '8px 12px', borderRadius: '8px', border: active ? '1px solid #ff6b2b' : '1px solid #1e1e35', background: active ? '#ff6b2b15' : 'transparent', color: active ? '#ff6b2b' : '#888', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }),
    badge: (color) => ({ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', background: color + '20', color: color }),
    stockBadge: (p) => {
      if (isOut(p)) return { bg: '#d6303120', color: '#d63031', label: 'OUT' };
      if (isLow(p)) return { bg: '#ffab4020', color: '#ffab40', label: 'LOW' };
      return { bg: '#00b89420', color: '#00b894', label: '' };
    },
    marginColor: (m) => m >= 60 ? '#00b894' : m >= 30 ? '#0984e3' : m >= 10 ? '#ffab40' : '#d63031',
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading inventory...</p></div>;

  // ════════════════════════════════════════════════════════════
  // ── RENDER ──
  // ════════════════════════════════════════════════════════════
  return (
    <div style={{ maxWidth: '100%' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 6px' }}>Inventory</h1>
          <p style={{ fontSize: '14px', color: '#888', margin: 0 }}>{products.length} products · ${(stats.totalValue || 0).toLocaleString()} retail value</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} style={{ padding: '10px 16px', background: '#d63031', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              🗑️ Delete {selectedIds.length}
            </button>
          )}
          <button onClick={startScanner} style={{ padding: '10px 16px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '10px', color: '#aaa', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            📷 Scan
          </button>
          <button onClick={() => { import('./lib/exportCSV').then(m => m.exportCSV('inventory')); }} style={{ padding: '10px 16px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '10px', color: '#aaa', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            📥 Export
          </button>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(emptyForm); }} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
            + Add Product
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Products', value: stats.total, color: '#ff6b2b' },
          { label: 'Retail Value', value: '$' + (stats.totalValue || 0).toLocaleString(), color: '#00b894' },
          { label: 'Cost Value', value: '$' + (stats.totalCost || 0).toLocaleString(), color: '#636e72' },
          { label: 'Avg Margin', value: margin + '%', color: S.marginColor(parseInt(margin)) },
          { label: 'Low Stock', value: stats.lowStock, color: stats.lowStock > 0 ? '#ffab40' : '#555', onClick: () => setView('lowstock') },
          { label: 'Out of Stock', value: stats.outOfStock, color: stats.outOfStock > 0 ? '#d63031' : '#555', onClick: () => setView('lowstock') },
        ].map((s, i) => (
          <div key={i} onClick={s.onClick} style={{ ...S.card, padding: '16px', cursor: s.onClick ? 'pointer' : 'default' }}>
            <p style={{ fontSize: '10px', color: '#666', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
            <p style={{ fontSize: '22px', fontWeight: '800', color: s.color, margin: 0 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── View Switcher + Search + Filters ── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px', background: '#1a1c2e', borderRadius: '10px', padding: '4px' }}>
          {[
            { key: 'table', icon: '☰', label: 'Table' },
            { key: 'grid', icon: '▦', label: 'Grid' },
            { key: 'categories', icon: '📁', label: 'Categories' },
            { key: 'lowstock', icon: '⚠️', label: 'Low Stock' },
          ].map(v => (
            <button key={v.key} onClick={() => setView(v.key)} style={S.viewBtn(view === v.key)}>
              {v.icon} {v.label}
            </button>
          ))}
        </div>
        <input placeholder="Search products, SKU, barcode, supplier..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ ...S.input, maxWidth: '280px', flex: '1 1 200px' }} />
        {view !== 'lowstock' && (
          <div style={{ display: 'flex', gap: '3px', background: '#1a1c2e', borderRadius: '10px', padding: '4px', flexWrap: 'wrap' }}>
            {categories.map(c => <button key={c} onClick={() => setCatFilter(c)} style={S.pill(catFilter === c)}>{c}</button>)}
          </div>
        )}
        {(view === 'table' || view === 'grid') && (
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...S.input, maxWidth: '170px', cursor: 'pointer' }}>
            <option value="name">Sort: Name</option>
            <option value="price">Sort: Price ↓</option>
            <option value="stock">Sort: Stock ↑</option>
            <option value="value">Sort: Value ↓</option>
            <option value="margin">Sort: Margin ↓</option>
          </select>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── TABLE VIEW ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {view === 'table' && (
        filtered.length === 0 ? <EmptyState products={products} /> : (
          <div style={{ ...S.card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e1e35' }}>
                    <th style={{ padding: '12px 10px', textAlign: 'left', width: '36px' }}>
                      <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0}
                        onChange={toggleSelectAll} style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} />
                    </th>
                    {['Product', 'SKU', 'Category', 'Price', 'Cost', 'Margin', 'Stock', 'Supplier', 'Updated', ''].map(h => (
                      <th key={h} style={{ padding: '12px 10px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => {
                    var sb = S.stockBadge(p); var m = getMargin(p);
                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #1e1e3515', opacity: p.is_active === false ? 0.45 : 1 }}>
                        <td style={{ padding: '10px' }}>
                          <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => toggleSelect(p.id)}
                            style={{ width: '15px', height: '15px', accentColor: '#ff6b2b', cursor: 'pointer' }} />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{p.name}</span>
                          {p.is_active === false && <span style={{ ...S.badge('#666'), marginLeft: '6px' }}>INACTIVE</span>}
                          {p.description && <p style={{ color: '#555', fontSize: '11px', margin: '2px 0 0', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description}</p>}
                        </td>
                        <td style={{ padding: '10px', color: '#777', fontSize: '12px', fontFamily: 'monospace' }}>{p.sku || '—'}</td>
                        <td style={{ padding: '10px' }}><span style={S.badge('#888')}>{p.category || 'General'}</span></td>
                        <td style={{ padding: '10px', color: '#00b894', fontWeight: '700', fontSize: '13px' }}>${parseFloat(p.retail_price || 0).toFixed(2)}</td>
                        <td style={{ padding: '10px', color: '#888', fontSize: '12px' }}>${parseFloat(p.cost || 0).toFixed(2)}</td>
                        <td style={{ padding: '10px' }}><span style={{ color: S.marginColor(m), fontWeight: '700', fontSize: '12px' }}>{m}%</span></td>
                        <td style={{ padding: '10px' }}>
                          {p.track_inventory !== false ? (
                            <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', background: sb.bg, color: sb.color }}
                              onClick={() => setStockAdj({ id: p.id, name: p.name, current: p.current_stock || 0, amount: '', reason: '' })}
                              title="Click to adjust">{p.current_stock || 0} {sb.label && <span style={{ fontSize: '10px' }}>{sb.label}</span>}</span>
                          ) : <span style={{ color: '#444', fontSize: '11px' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px', color: '#666', fontSize: '11px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.supplier_name || '—'}</td>
                        <td style={{ padding: '10px', color: '#555', fontSize: '11px' }}>
                          {p.updated_at ? daysSince(p.updated_at) + 'd ago' : '—'}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => handleEdit(p)} style={{ padding: '5px 10px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                            <button onClick={() => handleDelete(p.id)} style={{ padding: '5px 10px', background: '#d6303110', border: 'none', borderRadius: '6px', color: '#d63031', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Del</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableFooter filtered={filtered} products={products} />
          </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── GRID VIEW ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {view === 'grid' && (
        filtered.length === 0 ? <EmptyState products={products} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
            {filtered.map(p => {
              var sb = S.stockBadge(p); var m = getMargin(p); var profit = getProfit(p);
              return (
                <div key={p.id} style={{ ...S.card, opacity: p.is_active === false ? 0.45 : 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Image placeholder */}
                  <div style={{ width: '100%', height: '120px', background: 'linear-gradient(135deg, #252840, #16213e)', borderRadius: '10px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '36px', opacity: 0.3 }}>📦</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                      <h3 style={{ color: '#fff', fontSize: '14px', fontWeight: '700', margin: 0, flex: 1 }}>{p.name}</h3>
                      {p.is_active === false && <span style={S.badge('#666')}>OFF</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
                      <span style={S.badge('#888')}>{p.category || 'General'}</span>
                      {p.sku && <span style={{ color: '#555', fontSize: '11px', fontFamily: 'monospace' }}>{p.sku}</span>}
                    </div>
                    {/* Pricing */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                      <span style={{ color: '#00b894', fontWeight: '800', fontSize: '18px' }}>${parseFloat(p.retail_price || 0).toFixed(2)}</span>
                      <span style={{ color: S.marginColor(m), fontSize: '12px', fontWeight: '700' }}>{m}% margin</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#666', marginBottom: '12px' }}>
                      <span>Cost: ${parseFloat(p.cost || 0).toFixed(2)}</span>
                      <span>Profit: ${profit.toFixed(2)}</span>
                    </div>
                    {/* Stock */}
                    {p.track_inventory !== false ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span onClick={() => setStockAdj({ id: p.id, name: p.name, current: p.current_stock || 0, amount: '', reason: '' })}
                          style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', background: sb.bg, color: sb.color }}>
                          {p.current_stock || 0} in stock {sb.label && `· ${sb.label}`}
                        </span>
                        {p.low_stock_threshold && <span style={{ color: '#555', fontSize: '10px' }}>Alert at {p.low_stock_threshold}</span>}
                      </div>
                    ) : (
                      <p style={{ color: '#444', fontSize: '11px', marginBottom: '10px' }}>Stock not tracked</p>
                    )}
                    {p.supplier_name && <p style={{ color: '#555', fontSize: '11px', margin: '0 0 6px' }}>Supplier: {p.supplier_name}</p>}
                    {p.updated_at && <p style={{ color: '#444', fontSize: '10px', margin: '0 0 8px' }}>Updated {daysSince(p.updated_at)}d ago</p>}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #1e1e35' }}>
                    <button onClick={() => handleEdit(p)} style={{ flex: 1, padding: '8px', background: '#1e1e35', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => setStockAdj({ id: p.id, name: p.name, current: p.current_stock || 0, amount: '', reason: '' })}
                      style={{ flex: 1, padding: '8px', background: '#0984e315', border: 'none', borderRadius: '8px', color: '#0984e3', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>± Stock</button>
                    <button onClick={() => handleDelete(p.id)} style={{ padding: '8px 12px', background: '#d6303110', border: 'none', borderRadius: '8px', color: '#d63031', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── CATEGORIES VIEW ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {view === 'categories' && (
        Object.keys(grouped).length === 0 ? <EmptyState products={products} /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, items]) => {
              var isExpanded = expandedCats[cat] !== false; // default expanded
              var catValue = items.reduce((s, p) => s + (parseFloat(p.retail_price) || 0) * (p.current_stock || 0), 0);
              var catLow = items.filter(p => isLow(p) || isOut(p)).length;
              return (
                <div key={cat} style={S.card}>
                  <div onClick={() => setExpandedCats(prev => ({ ...prev, [cat]: !isExpanded }))}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: '700', margin: 0 }}>{cat}</h3>
                      <span style={{ color: '#666', fontSize: '13px' }}>{items.length} items</span>
                      {catLow > 0 && <span style={S.badge('#ffab40')}>⚠️ {catLow} low</span>}
                    </div>
                    <span style={{ color: '#00b894', fontWeight: '700', fontSize: '14px' }}>${catValue.toLocaleString()}</span>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                      {items.map(p => {
                        var sb = S.stockBadge(p); var m = getMargin(p);
                        return (
                          <div key={p.id} style={{ background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '12px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: p.is_active === false ? 0.45 : 1 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{p.name}</span>
                                {p.sku && <span style={{ color: '#555', fontSize: '10px', fontFamily: 'monospace' }}>{p.sku}</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                                <span style={{ color: '#00b894', fontWeight: '700' }}>${parseFloat(p.retail_price || 0).toFixed(2)}</span>
                                <span style={{ color: S.marginColor(m) }}>{m}%</span>
                                {p.supplier_name && <span style={{ color: '#555' }}>{p.supplier_name}</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {p.track_inventory !== false && (
                                <span onClick={() => setStockAdj({ id: p.id, name: p.name, current: p.current_stock || 0, amount: '', reason: '' })}
                                  style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', background: sb.bg, color: sb.color }}>
                                  {p.current_stock || 0}
                                </span>
                              )}
                              <button onClick={() => handleEdit(p)} style={{ padding: '4px 10px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── LOW STOCK VIEW ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {view === 'lowstock' && (
        lowStockItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 24px', ...S.card }}>
            <p style={{ fontSize: '40px', marginBottom: '12px' }}>✅</p>
            <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>All stocked up!</p>
            <p style={{ color: '#888', fontSize: '14px' }}>No products need restocking right now</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '4px' }}>
              <span style={S.badge('#d63031')}>🔴 {lowStockItems.filter(p => isOut(p)).length} out of stock</span>
              <span style={S.badge('#ffab40')}>⚠️ {lowStockItems.filter(p => isLow(p)).length} low stock</span>
            </div>
            {lowStockItems.map(p => {
              var outOfStock = isOut(p);
              var needed = (p.low_stock_threshold || 5) - (p.current_stock || 0);
              return (
                <div key={p.id} style={{ ...S.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderColor: outOfStock ? '#d6303140' : '#ffab4030' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '18px' }}>{outOfStock ? '🔴' : '⚠️'}</span>
                      <span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>{p.name}</span>
                      <span style={S.badge('#888')}>{p.category || 'General'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', marginLeft: '34px' }}>
                      <span style={{ color: outOfStock ? '#d63031' : '#ffab40', fontWeight: '700' }}>
                        {outOfStock ? 'OUT OF STOCK' : `${p.current_stock} left`} · Alert at {p.low_stock_threshold || 5}
                      </span>
                      <span style={{ color: '#666' }}>Need {needed > 0 ? '+' + needed : '—'} to clear alert</span>
                      {p.supplier_name && <span style={{ color: '#555' }}>Supplier: {p.supplier_name}</span>}
                      {p.cost > 0 && <span style={{ color: '#555' }}>Cost: ${parseFloat(p.cost).toFixed(2)} ea</span>}
                      {p.updated_at && <span style={{ color: '#444' }}>Last updated {daysSince(p.updated_at)}d ago</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={() => setStockAdj({ id: p.id, name: p.name, current: p.current_stock || 0, amount: String(needed > 0 ? needed : ''), reason: 'Restock' })}
                      style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                      + Restock
                    </button>
                    <button onClick={() => handleEdit(p)} style={{ padding: '8px 12px', background: '#1e1e35', border: 'none', borderRadius: '8px', color: '#aaa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                  </div>
                </div>
              );
            })}
            {/* Reorder Summary */}
            <div style={{ ...S.card, marginTop: '8px', background: '#1a1c2e' }}>
              <h4 style={{ color: '#888', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', marginBottom: '10px' }}>Reorder Summary</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                <div>
                  <p style={{ color: '#555', fontSize: '11px' }}>Items to restock</p>
                  <p style={{ color: '#fff', fontSize: '20px', fontWeight: '800' }}>{lowStockItems.length}</p>
                </div>
                <div>
                  <p style={{ color: '#555', fontSize: '11px' }}>Est. restock cost</p>
                  <p style={{ color: '#ffab40', fontSize: '20px', fontWeight: '800' }}>
                    ${lowStockItems.reduce((sum, p) => {
                      var needed2 = Math.max(0, (p.low_stock_threshold || 5) - (p.current_stock || 0));
                      return sum + needed2 * (parseFloat(p.cost) || 0);
                    }, 0).toFixed(0)}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#555', fontSize: '11px' }}>Potential revenue at risk</p>
                  <p style={{ color: '#d63031', fontSize: '20px', fontWeight: '800' }}>
                    ${lowStockItems.filter(p => isOut(p)).reduce((sum, p) => sum + (parseFloat(p.retail_price) || 0), 0).toFixed(0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── STOCK ADJUSTMENT MODAL ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {stockAdj.id && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000aa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
          onClick={() => setStockAdj({ id: null, name: '', current: 0, amount: '', reason: '' })}>
          <div style={{ ...S.card, maxWidth: '420px', width: '100%', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>Adjust Stock</h3>
            <p style={{ color: '#888', fontSize: '14px', marginBottom: '6px' }}>{stockAdj.name}</p>
            <p style={{ color: '#555', fontSize: '13px', marginBottom: '20px' }}>Current: <strong style={{ color: '#fff' }}>{stockAdj.current}</strong></p>
            <label style={S.label}>Quantity (+/-)</label>
            <input type="number" value={stockAdj.amount} onChange={e => setStockAdj({ ...stockAdj, amount: e.target.value })}
              placeholder="e.g. 10 or -3" style={{ ...S.input, marginBottom: '6px' }} autoFocus />
            {stockAdj.amount && (
              <p style={{ fontSize: '12px', color: '#888', marginBottom: '12px' }}>
                New stock: <strong style={{ color: '#fff' }}>{Math.max(0, stockAdj.current + parseInt(stockAdj.amount || 0))}</strong>
              </p>
            )}
            <label style={S.label}>Reason</label>
            <select value={stockAdj.reason} onChange={e => setStockAdj({ ...stockAdj, reason: e.target.value })} style={{ ...S.input, marginBottom: '6px', cursor: 'pointer' }}>
              <option value="">Select reason...</option>
              <option value="Restock">Restock / Delivery</option>
              <option value="Sold">Sold</option>
              <option value="Damaged">Damaged / Broken</option>
              <option value="Returned">Customer Return</option>
              <option value="Correction">Count Correction</option>
              <option value="Shrinkage">Shrinkage / Loss</option>
              <option value="Other">Other</option>
            </select>
            {stockAdj.reason === 'Other' && (
              <input value={stockAdj.customReason || ''} onChange={e => setStockAdj({ ...stockAdj, customReason: e.target.value })}
                placeholder="Describe reason..." style={{ ...S.input, marginTop: '8px' }} />
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={() => setStockAdj({ id: null, name: '', current: 0, amount: '', reason: '' })}
                style={{ flex: 1, padding: '12px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleStockAdjust}
                style={{ flex: 1, padding: '12px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>Adjust</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── ADD / EDIT PRODUCT MODAL ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000aa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}
          onClick={() => { setShowForm(false); setEditing(null); }}>
          <div style={{ ...S.card, maxWidth: '560px', width: '100%', maxHeight: '85vh', overflowY: 'auto', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '24px' }}>{editing ? 'Edit Product' : 'New Product'}</h3>
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={S.label}>Product Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Brake Pad, Lavender Oil" style={S.input} autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div><label style={S.label}>SKU</label><input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="Optional" style={S.input} /></div>
                <div><label style={S.label}>Barcode</label><input value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} placeholder="Optional" style={S.input} /></div>
                <div><label style={S.label}>Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ ...S.input, cursor: 'pointer' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={S.label}>Retail Price ($)</label><input type="number" step="0.01" value={form.retail_price} onChange={e => setForm({ ...form, retail_price: e.target.value })} placeholder="0.00" style={S.input} /></div>
                <div><label style={S.label}>Cost ($)</label><input type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} placeholder="0.00" style={S.input} /></div>
              </div>
              {form.retail_price && form.cost && (
                <div style={{ display: 'flex', gap: '16px', padding: '10px 14px', background: '#1a1c2e', borderRadius: '8px', fontSize: '12px' }}>
                  <span style={{ color: '#888' }}>Profit: <strong style={{ color: '#00b894' }}>${(parseFloat(form.retail_price || 0) - parseFloat(form.cost || 0)).toFixed(2)}</strong></span>
                  <span style={{ color: '#888' }}>Margin: <strong style={{ color: S.marginColor(parseFloat(form.retail_price) > 0 ? Math.round(((parseFloat(form.retail_price) - parseFloat(form.cost || 0)) / parseFloat(form.retail_price)) * 100) : 0) }}>
                    {parseFloat(form.retail_price) > 0 ? Math.round(((parseFloat(form.retail_price) - parseFloat(form.cost || 0)) / parseFloat(form.retail_price)) * 100) : 0}%
                  </strong></span>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div><label style={S.label}>Current Stock</label><input type="number" value={form.current_stock} onChange={e => setForm({ ...form, current_stock: e.target.value })} placeholder="0" style={S.input} /></div>
                <div><label style={S.label}>Low Stock Alert At</label><input type="number" value={form.low_stock_threshold} onChange={e => setForm({ ...form, low_stock_threshold: e.target.value })} placeholder="5" style={S.input} /></div>
              </div>
              <div><label style={S.label}>Supplier</label><input value={form.supplier_name} onChange={e => setForm({ ...form, supplier_name: e.target.value })} placeholder="e.g. BikeMaster, DoTerra" style={S.input} /></div>
              <div style={{ display: 'flex', gap: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.track_inventory} onChange={e => setForm({ ...form, track_inventory: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#ff6b2b' }} />
                  Track stock levels
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#aaa', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ width: '16px', height: '16px', accentColor: '#ff6b2b' }} />
                  Active
                </label>
              </div>
              <div><label style={S.label}>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional notes..." rows="2" style={{ ...S.input, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSave} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>{editing ? 'Save Changes' : 'Add Product'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════ */}
      {/* ── BARCODE SCANNER MODAL ── */}
      {/* ════════════════════════════════════════════════════════════ */}
      {showScanner && (
        <div style={{ position: 'fixed', inset: 0, background: '#000000dd', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
          <div style={{ ...S.card, maxWidth: '480px', width: '100%', padding: '28px', textAlign: 'center' }}>
            <h3 style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>📷 Scan Barcode</h3>
            <div style={{ width: '100%', height: '240px', background: '#000', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', position: 'relative' }}>
              <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'cover' }} playsInline />
              <div style={{ position: 'absolute', inset: '30%', border: '2px solid #ff6b2b', borderRadius: '8px', opacity: 0.7 }} />
            </div>
            <p style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>Point camera at barcode, or type manually:</p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input id="barcodeInput" placeholder="Enter barcode or SKU..." style={{ ...S.input, flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') handleBarcodeLookup(e.target.value); }} />
              <button onClick={() => { var v = document.getElementById('barcodeInput').value; if (v) handleBarcodeLookup(v); }}
                style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer' }}>Look Up</button>
            </div>
            <button onClick={stopScanner} style={{ marginTop: '16px', padding: '10px 24px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer' }}>Close Scanner</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub Components ──
function EmptyState({ products }) {
  return (
    <div style={{ textAlign: 'center', padding: '64px 24px', background: '#12121f', borderRadius: '20px', border: '1px solid #1e1e35' }}>
      <p style={{ fontSize: '40px', marginBottom: '12px' }}>📦</p>
      <p style={{ color: '#fff', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
        {products.length === 0 ? 'No products yet' : 'No matching products'}
      </p>
      <p style={{ color: '#888', fontSize: '14px' }}>
        {products.length === 0 ? 'Add your first product to start tracking inventory' : 'Try adjusting your search or filters'}
      </p>
    </div>
  );
}

function TableFooter({ filtered, products }) {
  return (
    <div style={{ padding: '10px 14px', borderTop: '1px solid #1e1e35', display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
      <span>Showing {filtered.length} of {products.length}</span>
      <span style={{ color: '#00b894' }}>
        Value: ${filtered.reduce((s, p) => s + (parseFloat(p.retail_price) || 0) * (p.current_stock || 0), 0).toLocaleString()}
      </span>
    </div>
  );
}

export default Inventory;
